/**
 * Conference note categories (global, shared across all students).
 *
 * Stored as root-level `conference_categories/{catId}` docs — the same shape as
 * the existing `announcement_categories` / `subject_categories` collections:
 * readable by any signed-in user, writable by admins only (see firestore.rules).
 *
 * Notes reference a category by **id** (not name) so an admin renaming or
 * recolouring a category updates every existing note at once. Category ids are
 * resolved live from this subscription, never denormalised onto the note.
 */
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../../../../firebaseConfig";

export const CONFERENCE_CATEGORY_COLLECTION = "conference_categories";

// Used when a category has no/invalid colour stored.
export const DEFAULT_CATEGORY_COLOR = "#64748b";

// Swatches offered in the manager. A native colour picker covers anything else.
export const CATEGORY_COLOR_PRESETS = [
  "#ef4444",
  "#f97316",
  "#f59e0b",
  "#84cc16",
  "#22c55e",
  "#14b8a6",
  "#06b6d4",
  "#3b82f6",
  "#6366f1",
  "#8b5cf6",
  "#d946ef",
  "#ec4899",
  "#64748b",
];

const HEX_COLOR_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

export function isValidCategoryColor(value) {
  return HEX_COLOR_RE.test(String(value || "").trim());
}

export function normalizeCategoryColor(value) {
  const clean = String(value || "").trim();
  return isValidCategoryColor(clean) ? clean.toLowerCase() : DEFAULT_CATEGORY_COLOR;
}

export function normalizeCategoryName(value) {
  return String(value || "").trim().replace(/\s+/g, " ");
}

export function categoryNameKey(value) {
  return normalizeCategoryName(value).toLowerCase();
}

/** Live list of categories, ordered by name. Returns the unsubscribe function. */
export function subscribeConferenceCategories(onRows, onError) {
  const q = query(
    collection(db, CONFERENCE_CATEGORY_COLLECTION),
    orderBy("name", "asc")
  );
  return onSnapshot(
    q,
    (snap) => {
      onRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    },
    (err) => {
      if (typeof onError === "function") onError(err);
    }
  );
}

export async function addConferenceCategory({ name, color }) {
  const clean = normalizeCategoryName(name);
  if (!clean) throw new Error("Category name is required.");
  const ref = await addDoc(collection(db, CONFERENCE_CATEGORY_COLLECTION), {
    name: clean,
    color: normalizeCategoryColor(color),
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateConferenceCategory(id, { name, color }) {
  if (!id) throw new Error("Missing category id.");
  const patch = {};
  if (name !== undefined) {
    const clean = normalizeCategoryName(name);
    if (!clean) throw new Error("Category name is required.");
    patch.name = clean;
  }
  if (color !== undefined) patch.color = normalizeCategoryColor(color);
  if (!Object.keys(patch).length) return;
  await updateDoc(doc(db, CONFERENCE_CATEGORY_COLLECTION, id), patch);
}

export async function deleteConferenceCategory(id) {
  if (!id) throw new Error("Missing category id.");
  await deleteDoc(doc(db, CONFERENCE_CATEGORY_COLLECTION, id));
}

/**
 * Reads the tint for a chip. `soft` mixes the colour toward white so the tag
 * stays legible on the light dashboard surface.
 */
export function categoryChipStyle(color, { soft = true } = {}) {
  const base = normalizeCategoryColor(color);
  return {
    color: base,
    borderColor: base,
    background: soft ? `${base}1a` : "#fff", // 1a ≈ 10% alpha
  };
}
